import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EyeIcon, EyeOffIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/lib/api'

const changePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

const setPasswordFormSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ChangePasswordFormValues = z.infer<typeof changePasswordFormSchema>
type SetPasswordFormValues = z.infer<typeof setPasswordFormSchema>

interface SetupStatusResponse {
  success: boolean
  data?: {
    isFirstTimeSetup: boolean
    requiresPasswordChange: boolean
  }
}

interface ChangePasswordResponse {
  success: boolean
  error?: string
}

export function ChangePasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [setupStatus, setSetupStatus] = useState<{ isFirstTimeSetup: boolean; requiresPasswordChange: boolean } | null>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const required = searchParams.get('required') === 'true'

  const { user, logout } = useAuth()
  const firstTime = setupStatus?.isFirstTimeSetup ?? false

  const logo = useMemo(() => '/logo.png', [])

  useEffect(() => {
    const fetchSetupStatus = async () => {
      try {
        const result = await api.post<SetupStatusResponse>('/auth/setup-status')
        if (result.success && result.data) {
          setSetupStatus(result.data)
        } else {
          setSetupStatus({ isFirstTimeSetup: false, requiresPasswordChange: false })
        }
      } catch (err) {
        console.error('Failed to fetch setup status', err)
        setSetupStatus({ isFirstTimeSetup: false, requiresPasswordChange: false })
      }
    }

    fetchSetupStatus()
  }, [])

  const form = useForm<ChangePasswordFormValues | SetPasswordFormValues>({
    resolver: zodResolver(firstTime ? setPasswordFormSchema : changePasswordFormSchema),
    defaultValues: firstTime
      ? {
          newPassword: '',
          confirmPassword: '',
        }
      : {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
  })

  useEffect(() => {
    if (!user && !firstTime) {
      navigate('/login')
    } else if (!user && firstTime) {
      const timer = setTimeout(() => {
        if (!user) {
          navigate('/login')
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [user, navigate, firstTime])

  const onSubmit = async (values: ChangePasswordFormValues | SetPasswordFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const payload = firstTime
        ? {
            newPassword: values.newPassword,
            firstTime: true,
          }
        : {
            currentPassword: (values as ChangePasswordFormValues).currentPassword,
            newPassword: values.newPassword,
            firstTime: false,
          }

      const result = await api.post<ChangePasswordResponse>('/auth/change-password', payload)

      if (result.success) {
        setSuccess(true)

        if (firstTime) {
          setTimeout(() => {
            navigate('/dashboard')
          }, 2000)
        } else if (required) {
          setTimeout(() => {
            logout()
            navigate('/login?message=Password changed successfully. Please log in again.')
          }, 2000)
        } else {
          setTimeout(() => {
            navigate('/dashboard')
          }, 2000)
        }
      } else {
        setError(result.error || 'Failed to change password')
      }
    } catch (err) {
      console.error('Password change failed', err)
      setError('Failed to change password')
    } finally {
      setIsLoading(false)
    }
  }

  if (!user && !firstTime) {
    return null
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-clean p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src={logo} alt="Gathio Logo" className="w-8 h-8" />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-3 tracking-tight">
            {firstTime ? 'Set Your Password' : 'Change Password'}
          </h2>
          {required ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl mb-4">
              <p className="text-sm text-yellow-800">
                {firstTime
                  ? 'Welcome! Please set a secure password to complete your account setup.'
                  : 'For security reasons, you must change your password before continuing.'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {firstTime
                ? 'Set a secure password to finalize your account.'
                : 'Keep your account secure by updating your password regularly.'}
            </p>
          )}
        </div>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm text-green-800">
              Password updated successfully!{' '}
              {required ? 'You will be redirected shortly.' : 'Redirecting to your dashboard...'}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {!firstTime && (
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground mb-2 block">
                      Current Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="••••••••"
                          type={showCurrentPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          disabled={isLoading}
                          className="h-14 px-4 pr-12 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showCurrentPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-xs mt-1 text-destructive" />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">
                    New Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="••••••••"
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="h-14 px-4 pr-12 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showNewPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs mt-1 text-destructive" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="••••••••"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="h-14 px-4 pr-12 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs mt-1 text-destructive" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl disabled:opacity-50"
              disabled={isLoading || success}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Updating password...
                </div>
              ) : success ? (
                'Password Updated!'
              ) : (
                firstTime ? 'Set Password' : 'Update Password'
              )}
            </Button>
          </form>
        </Form>

        {!required && !firstTime && (
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              Forgot your password?{' '}
              <Link to="/forgot-password" className="text-primary hover:underline">
                Reset it here
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChangePasswordPage
